import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
    Armchair,
    ArrowDown,
    ArrowRight,
    ArrowUpRight,
    Award,
    BadgeCheck,
    Bath,
    BedDouble,
    Calendar,
    Check,
    ChevronsLeftRight,
    CircleCheck,
    Clock,
    Droplet,
    Droplets,
    Eye,
    Flame,
    Footprints,
    Gift,
    Globe,
    Grid3x3,
    Hammer,
    Heart,
    House,
    Info,
    Lamp,
    Layers,
    Leaf,
    Mail,
    MapPin,
    Maximize2,
    MousePointerClick,
    Package,
    Paintbrush,
    Palette,
    Percent,
    Phone,
    Quote,
    Recycle,
    Ruler,
    Shield,
    ShieldCheck,
    ShoppingBag,
    Snowflake,
    Sofa,
    Sparkles,
    Star,
    Sun,
    Tag,
    Trees,
    Truck,
    Waves,
    Wrench,
    type LucideIcon,
} from 'lucide-react';

/**
 * The icon set: the marks a surface/furniture brand reaches for on a post —
 * specs (ruler, maximise), properties (water, sun, frost, fire, slip), rooms,
 * service (delivery, warranty, contact) and calls to action.
 *
 * Rendered from lucide's own components to an SVG string, then parsed by
 * Fabric into plain paths — no icon font, no custom object type.
 */
export interface IconDef {
    id: string;
    Icon: LucideIcon;
    /** Search words (English; the panel's search also matches the translated label). */
    tags: string;
}

export const ICONS: IconDef[] = [
    { id: 'ruler', Icon: Ruler, tags: 'size dimension measure spec' },
    { id: 'maximize', Icon: Maximize2, tags: 'size large format expand' },
    { id: 'grid', Icon: Grid3x3, tags: 'tile pattern layout' },
    { id: 'layers', Icon: Layers, tags: 'thickness stack collection' },
    { id: 'droplet', Icon: Droplet, tags: 'water resistant waterproof' },
    { id: 'droplets', Icon: Droplets, tags: 'water stain resistant' },
    { id: 'sun', Icon: Sun, tags: 'light uv outdoor' },
    { id: 'snowflake', Icon: Snowflake, tags: 'frost resistant outdoor' },
    { id: 'flame', Icon: Flame, tags: 'heat fire resistant' },
    { id: 'footprints', Icon: Footprints, tags: 'slip resistant anti-slip' },
    { id: 'shield', Icon: Shield, tags: 'durable protection' },
    { id: 'shield-check', Icon: ShieldCheck, tags: 'warranty guarantee' },
    { id: 'leaf', Icon: Leaf, tags: 'eco natural sustainable' },
    { id: 'recycle', Icon: Recycle, tags: 'eco recycled sustainable' },
    { id: 'trees', Icon: Trees, tags: 'wood oak natural' },
    { id: 'waves', Icon: Waves, tags: 'pool water texture' },
    { id: 'sparkles', Icon: Sparkles, tags: 'new shine premium' },
    { id: 'star', Icon: Star, tags: 'review rating favourite' },
    { id: 'heart', Icon: Heart, tags: 'love favourite' },
    { id: 'award', Icon: Award, tags: 'quality award premium' },
    { id: 'badge-check', Icon: BadgeCheck, tags: 'certified verified quality' },
    { id: 'check', Icon: Check, tags: 'yes included feature' },
    { id: 'circle-check', Icon: CircleCheck, tags: 'yes included feature' },
    { id: 'house', Icon: House, tags: 'home interior room' },
    { id: 'sofa', Icon: Sofa, tags: 'living room furniture' },
    { id: 'armchair', Icon: Armchair, tags: 'furniture chair seating' },
    { id: 'bed', Icon: BedDouble, tags: 'bedroom furniture' },
    { id: 'bath', Icon: Bath, tags: 'bathroom' },
    { id: 'lamp', Icon: Lamp, tags: 'lighting furniture' },
    { id: 'paintbrush', Icon: Paintbrush, tags: 'design colour finish' },
    { id: 'palette', Icon: Palette, tags: 'colour colours range' },
    { id: 'hammer', Icon: Hammer, tags: 'installation craft' },
    { id: 'wrench', Icon: Wrench, tags: 'installation service' },
    { id: 'truck', Icon: Truck, tags: 'delivery shipping' },
    { id: 'package', Icon: Package, tags: 'delivery box order' },
    { id: 'gift', Icon: Gift, tags: 'offer gift sale' },
    { id: 'tag', Icon: Tag, tags: 'price sale offer' },
    { id: 'percent', Icon: Percent, tags: 'sale discount offer' },
    { id: 'shopping-bag', Icon: ShoppingBag, tags: 'shop buy store' },
    { id: 'clock', Icon: Clock, tags: 'time hours limited' },
    { id: 'calendar', Icon: Calendar, tags: 'date event launch' },
    { id: 'map-pin', Icon: MapPin, tags: 'location showroom address' },
    { id: 'phone', Icon: Phone, tags: 'contact call' },
    { id: 'mail', Icon: Mail, tags: 'contact email' },
    { id: 'globe', Icon: Globe, tags: 'website online' },
    { id: 'eye', Icon: Eye, tags: 'visualise view see' },
    { id: 'pointer', Icon: MousePointerClick, tags: 'click cta link' },
    { id: 'arrow-right', Icon: ArrowRight, tags: 'swipe next cta' },
    { id: 'arrow-up-right', Icon: ArrowUpRight, tags: 'link cta external' },
    { id: 'arrow-down', Icon: ArrowDown, tags: 'scroll down' },
    { id: 'compare', Icon: ChevronsLeftRight, tags: 'before after compare slider' },
    { id: 'quote', Icon: Quote, tags: 'testimonial review' },
    { id: 'info', Icon: Info, tags: 'information tip' },
];

const svgCache = new Map<string, string>();

/** The icon as an SVG string at 24×24, ready for fabric.loadSVGFromString. */
export function iconSvg(id: string): string | null {
    const hit = svgCache.get(id);
    if (hit) return hit;
    const def = ICONS.find((i) => i.id === id);
    if (!def) return null;
    const markup = renderToStaticMarkup(
        React.createElement(def.Icon, { size: 24, strokeWidth: 1.75, color: '#000000' }),
    );
    svgCache.set(id, markup);
    return markup;
}
